import React, { useState } from 'react';
import { View } from 'react-native';
import { CalcShell, FormulaLine, FormulaNote, NumericField } from '../../../components/shell';
import { DiagramOutsideAirPercentage } from '../../../components/diagrams';
import { usePersistedState } from '../../../hooks/usePersistedState';
import { useUnits } from '../../../context/UnitsContext';

const KEY = 'tabai.calc.airTempOutsideAirPct.v1';

type Fields = { outsideAirFlow: string; totalAirFlow: string; outsideAirPercentage: string };
const EMPTY: Fields = { outsideAirFlow: '', totalAirFlow: '', outsideAirPercentage: '' };

/**
 * Outside Air Percentage — OA% = (OA / Total) × 100
 */
const AirTemperatureOutsideAirPercentageScreen = () => {
  const [fields, setFields, { clear: clearPersisted }] = usePersistedState<Fields>(KEY, EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [solvedFor, setSolvedFor] = useState<keyof Fields | null>(null);
  const { unit, toImp, fromImp } = useUnits();

  const set = (patch: Partial<Fields>) => setFields((prev) => ({ ...prev, ...patch }));

  const calculate = () => {
    setError(null);
    const filled = (Object.values(fields) as string[]).filter((v) => v !== '').length;
    if (filled < 2) {
      setError('Fill any 2 of the 3 fields to solve for the missing one.');
      return;
    }
    if (filled === 3) {
      setError('All 3 values are filled — clear one to solve for it.');
      return;
    }

    const oa = toImp(fields.outsideAirFlow, 'flow');
    const total = toImp(fields.totalAirFlow, 'flow');
    const oaPercent = parseFloat(fields.outsideAirPercentage);

    if (fields.outsideAirPercentage === '') {
      if (total <= 0) {
        setError('Total air flow must be greater than zero.');
        return;
      }
      if (oa > total) {
        setError('Outside air flow cannot exceed total air flow.');
        return;
      }
      setSolvedFor('outsideAirPercentage');
      set({ outsideAirPercentage: ((oa / total) * 100).toFixed(1) });
    } else if (fields.outsideAirFlow === '') {
      if (oaPercent < 0 || oaPercent > 100) {
        setError('Outside air percentage must be between 0 and 100%.');
        return;
      }
      setSolvedFor('outsideAirFlow');
      set({ outsideAirFlow: fromImp((total * oaPercent) / 100, 'flow', 1) });
    } else if (fields.totalAirFlow === '') {
      if (oaPercent <= 0 || oaPercent > 100) {
        setError('Outside air percentage must be between 0 and 100%.');
        return;
      }
      setSolvedFor('totalAirFlow');
      set({ totalAirFlow: fromImp((oa * 100) / oaPercent, 'flow', 1) });
    }
  };

  const clearAll = () => {
    setError(null);
    setSolvedFor(null);
    void clearPersisted();
  };

  const result = [
    fields.outsideAirFlow && { label: 'Outside Air Flow', value: fields.outsideAirFlow, unit: unit('flow'), emphasis: solvedFor === 'outsideAirFlow' },
    fields.totalAirFlow && { label: 'Total Air Flow', value: fields.totalAirFlow, unit: unit('flow'), emphasis: solvedFor === 'totalAirFlow' },
    fields.outsideAirPercentage && { label: 'OA Percentage', value: fields.outsideAirPercentage, unit: '%', emphasis: solvedFor === 'outsideAirPercentage' },
  ].filter(Boolean) as { label: string; value: string; unit?: string; emphasis?: boolean }[];

  return (
    <CalcShell
      title="Outside Air Percentage"
      subtitle="Calculate outside air percentage based on outside air flow and total air flow"
      formula={
        <>
          <FormulaLine>OA% = (OA Flow / Total Flow) × 100</FormulaLine>
          <FormulaNote>
            OA% = Outside Air Percentage (%), OA Flow = Outside Air Flow ({unit('flow')}), Total Flow = Total Air Flow ({unit('flow')})
          </FormulaNote>
        </>
      }
      error={error}
      onCalculate={calculate}
      onClear={clearAll}
      result={result.length ? result : null}
      copyFooter="Formula: OA% = (OA Flow / Total Flow) × 100"
      diagram={<DiagramOutsideAirPercentage />}
      outputSlots={{
        outside_air_pct: fields.outsideAirPercentage ? parseFloat(fields.outsideAirPercentage) : undefined,
      }}
      onPrefill={(values) => {
        const patch: Partial<Fields> = {};
        if (values.outside_air_pct != null && !fields.outsideAirPercentage) {
          patch.outsideAirPercentage = values.outside_air_pct.toFixed(1);
        }
        if (Object.keys(patch).length > 0) set(patch);
      }}
    >
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label="Outside Air Flow"
          value={fields.outsideAirFlow}
          onChangeText={(v) => set({ outsideAirFlow: v })}
          rightText={unit('flow')}
          maxDecimals={1}
          onSubmitEditing={calculate}
        />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label="Total Air Flow"
          value={fields.totalAirFlow}
          onChangeText={(v) => set({ totalAirFlow: v })}
          rightText={unit('flow')}
          maxDecimals={1}
          onSubmitEditing={calculate}
        />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label="Outside Air Percentage"
          value={fields.outsideAirPercentage}
          onChangeText={(v) => set({ outsideAirPercentage: v })}
          rightText="%"
          maxDecimals={1}
          onSubmitEditing={calculate}
        />
      </View>
    </CalcShell>
  );
};

export default AirTemperatureOutsideAirPercentageScreen;
